import { FormResultForEmployeeProps } from "@/components/form-result/result-modal/components/types.ts";
import {
  getWriteToFieldFields,
  getWriteToFieldTable,
} from "@/components/questionnaire/components/write-to-field-element.utils.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useCountries } from "@/queries/use-locations.query.ts";
import { useOrganizationListByType } from "@/queries/use-organization-lists.query.ts";
import { ApiCustomField, ApiFieldTypes } from "@/types/custom-field.types.ts";
import { ApiFormElementWriteToFieldTargetType } from "@/types/forms/form-edit.types.ts";
import { ApiOrganizationListType } from "@/types/organization-list.types.ts";
import { PermissionType } from "@/types/role.types.ts";
import { Box, ListItem, Stack, Text, Title } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CompensationLinesValue } from "@/components/questionnaire/components/CompensationLinesValue.tsx";

export const WriteToFieldElementResult = ({ element, answer }: FormResultForEmployeeProps) => {
  const { t } = useTranslation();
  const { renderDate } = useFormatter();
  const { getFieldTitle, renderFieldDataInCell } = useCustomFieldsHook();
  const settings = element.write_to_field_settings;
  const fields = useMemo(
    () => (settings ? getWriteToFieldFields(element, settings) : []),
    [element, settings]
  );
  const rows = answer?.answer_write_to_field ?? [];
  const hasTerminationLeaveType = fields.some(
    (field) => field.type === ApiFieldTypes.employee_termination_leave_type
  );
  const hasTerminationReason = fields.some(
    (field) => field.type === ApiFieldTypes.employee_termination_reason
  );
  const { data: terminationLeaveTypes } = useOrganizationListByType(
    ApiOrganizationListType.termination_leave_types,
    hasTerminationLeaveType
  );
  const { data: terminationReasons } = useOrganizationListByType(
    ApiOrganizationListType.termination_reasons,
    hasTerminationReason
  );
  const fieldsForRendering = useMemo(
    () =>
      fields.map((field) => {
        const organizationList =
          field.type === ApiFieldTypes.employee_termination_leave_type
            ? terminationLeaveTypes
            : field.type === ApiFieldTypes.employee_termination_reason
              ? terminationReasons
              : undefined;

        if (!organizationList) return field;

        return {
          ...field,
          options: organizationList.items.map((item) => ({
            id: item.id,
            title: item.title,
            description: "",
            is_archived: item.is_archived,
          })),
        };
      }),
    [fields, terminationLeaveTypes, terminationReasons]
  );
  const hasCountryField = fieldsForRendering.some(
    (field) => field.type === ApiFieldTypes.employee_address_country
  );
  const { data: countries } = useCountries(hasCountryField);

  const targetTitle = useMemo(() => {
    if (!settings) return "";

    if (settings.target_type === ApiFormElementWriteToFieldTargetType.field) {
      const field = element.fields?.find((field) => field.id === settings.field_id);
      return field ? getFieldTitle(field) : "";
    }

    const table = element.tables?.find((table) => table.id === settings.table_id);
    return table?.title ?? "";
  }, [element.fields, element.tables, getFieldTitle, settings]);

  if (!settings) return null;

  const isHistoricalTable = !!getWriteToFieldTable(element, settings)?.is_historical;
  const shouldRenderSeparateEffectiveFrom =
    isHistoricalTable && !fields.some((field) => field.type === ApiFieldTypes.effective_from);

  return (
    <Stack gap={"md"}>
      <Title
        header={settings.title || t("surveys:write_to_field_title")}
        size={"sm"}
        paddingTop
        caption={t("surveys:write_to_field_results_destination", {
          target: targetTitle,
        })}
      />
      {rows.map((row, rowIndex) => {
        const rowCustomFields: ApiCustomField[] = fieldsForRendering.map((field) => {
          const cell = row.cells?.find(
            (cell) => cell.field_id === field.id && cell.field_type === field.type
          );

          return {
            field,
            permission_value: PermissionType.view,
            value:
              field.type === ApiFieldTypes.effective_from && row.date_effective_from
                ? { date_value: row.date_effective_from }
                : (cell?.value ?? null),
          };
        });

        return (
          <Stack key={`write-to-field-result-row-${rowIndex}`} gap={"xs"}>
            {rows.length > 1 && (
              <Text className={"text-secondary"}>
                {t("surveys:write_to_field_entry_title", { number: rowIndex + 1 })}
              </Text>
            )}
            {shouldRenderSeparateEffectiveFrom && row.date_effective_from && (
              <ListItem
                preTitle={t("common:date_effective_from")}
                title={renderDate(row.date_effective_from)}
              />
            )}
            <Box className={"flex flex-col gap-1"}>
              {fieldsForRendering.map((field) => {
                const cell = row.cells?.find(
                  (item) => item.field_id === field.id && item.field_type === field.type
                );
                if (field.type === ApiFieldTypes.employee_compensation_lines) {
                  return (
                    <ListItem
                      key={`write-to-field-result-${rowIndex}-${field.type}-${field.id}`}
                      preTitle={getFieldTitle(field)}
                      titleSlot={<CompensationLinesValue lines={cell?.value_compensation_lines} />}
                    />
                  );
                }
                const fieldValue = rowCustomFields.find(
                  (item) => item.field.id === field.id
                )?.value;
                const genderValue = fieldValue?.value_text;
                const countryId = fieldValue?.value_int_array?.[0] ?? fieldValue?.value_int;
                const country = countries?.find((country) => country.id === countryId);
                const title =
                  field.type === ApiFieldTypes.employee_gender && genderValue
                    ? t(`common:gender_${genderValue}`)
                    : field.type === ApiFieldTypes.employee_address_country
                      ? (country?.title ?? "-")
                      : renderFieldDataInCell(field, rowCustomFields);

                return (
                  <ListItem
                    key={`write-to-field-result-${rowIndex}-${field.type}-${field.id}`}
                    preTitle={getFieldTitle(field)}
                    title={typeof title === "string" ? title : undefined}
                    titleSlot={
                      typeof title === "string" ? undefined : (
                        <Text className={"font-semibold"}>{title}</Text>
                      )
                    }
                  />
                );
              })}
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};
